import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo, { JsonLd } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/news";

export default function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = activeCategory === "all" || item.category === activeCategory;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.excerpt.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        item.categoryName.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [keyword, activeCategory]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const kw = keyword.trim();
    if (kw) {
      setSearchParams({ q: kw });
    } else {
      setSearchParams({});
    }
  };

  const changeCategory = (slug: string) => {
    setActiveCategory(slug);
    setSearchParams(keyword.trim() ? { q: keyword.trim() } : {});
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "全部快讯 - 独家吃瓜",
    description: "独家吃瓜全部娱乐快讯列表，支持按明星八卦、综艺爆料、影视动态、网红热榜分类筛选与关键词搜索。",
    inLanguage: "zh-CN",
    url: "https://wxtmhb.cn/news",
    isPartOf: { "@type": "WebSite", name: "独家吃瓜" },
  };

  return (
    <>
      <Seo
        title="全部快讯 - 独家吃瓜今日动态汇总"
        description="独家吃瓜全部快讯列表，按明星八卦、综艺爆料、影视动态、网红热榜分类整理，支持关键词搜索，快速定位你想看的娱乐动态。"
        keywords="独家吃瓜快讯,娱乐资讯列表,明星八卦,综艺爆料,影视动态,网红热榜"
        path="/news"
      />
      <JsonLd data={collectionSchema} />
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "全部快讯" }]} />

          <header className="mb-7">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              全部娱乐快讯
            </h1>
            <p className="mt-3 text-sm text-foreground-600 font-label max-w-2xl">
              这里汇总了「独家吃瓜」全部已发布内容，你可以按分类筛选，也可以直接搜索艺人名字、节目名或关键词。
            </p>
          </header>

          <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 mb-7">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索关键词，例如「路透」「定档」「涨粉」"
                  className="w-full pl-10 pr-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors font-label"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-label hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => changeCategory("all")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300"
                }`}
              >
                全部
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => changeCategory(category.slug)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === category.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-foreground-500 font-label mb-4">
            共找到 <span className="text-primary-600 font-medium">{list.length}</span> 条内容
          </p>

          {list.length > 0 ? (
            <div className="space-y-4">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} variant="wide" />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-500 text-2xl mb-4">
                <i className="ri-search-eye-line"></i>
              </span>
              <h2 className="font-heading text-lg font-semibold text-foreground-900">
                没有找到相关内容
              </h2>
              <p className="mt-2 text-sm text-foreground-500 font-label">
                换个关键词试试，或切换到其他分类看看。
              </p>
            </div>
          )}
        </div>
      </SiteLayout>
    </>
  );
}